// Left-rail filters for the Failure Tracking
// page. URL-synced via the parent component; this component is purely
// presentational.

import { Card, FormField, Input } from "@/design-system/components/ui";
import type { CriteriaLane, FailureStatus } from "@/api/dealFlow/types";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface Props {
  statuses: FailureStatus[];
  lanes: CriteriaLane[];
  from: string;
  to: string;
  onChange: (next: Partial<Record<string, string | null>>) => void;
}

const STATUS_OPTIONS: { value: FailureStatus; label: string }[] = [
  { value: "Open", label: "Open" },
  { value: "Reviewed", label: "Reviewed" },
  { value: "Resolved", label: "Resolved" },
];

const LANE_OPTIONS: { value: CriteriaLane; label: string }[] = [
  { value: "A1", label: "A1 — Underwriter" },
  { value: "A2", label: "A2 — Financial Analyst" },
  { value: "A3", label: "A3 — Legal Analyst" },
  { value: "A4", label: "A4 — Business Analyst" },
];

export function FailureFilterSidebar({
  statuses,
  lanes,
  from,
  to,
  onChange,
}: Props) {
  const tz = useUserTimeZone();
  return (
    <Card className="p-space-4 flex flex-col gap-space-4" aria-label="Filters">
      <div role="group" aria-labelledby="status-filter-heading">
        <h2 id="status-filter-heading" className="font-display text-label uppercase tracking-label text-onyx mb-space-2">
          Status
        </h2>
        <div className="flex flex-col gap-space-1">
          {STATUS_OPTIONS.map((opt) => {
            const checked = statuses.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="inline-flex items-center gap-space-2 text-body text-onyx cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange({
                      status: checked
                        ? statuses.filter((s) => s !== opt.value).join(",") || null
                        : [...statuses, opt.value].join(","),
                    })
                  }
                  aria-label={`Filter by status ${opt.label}`}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div role="group" aria-labelledby="lane-filter-heading">
        <h2 id="lane-filter-heading" className="font-display text-label uppercase tracking-label text-onyx mb-space-2">
          Lane
        </h2>
        <div className="flex flex-col gap-space-1">
          {LANE_OPTIONS.map((opt) => {
            const checked = lanes.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="inline-flex items-center gap-space-2 text-body text-onyx cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange({
                      lane: checked
                        ? lanes.filter((l) => l !== opt.value).join(",") || null
                        : [...lanes, opt.value].join(","),
                    })
                  }
                  aria-label={`Filter by lane ${opt.label}`}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="font-display text-label uppercase tracking-label text-onyx mb-space-2">
          Date range
        </h2>
        <div className="flex flex-col gap-space-2">
          <FormField htmlFor="failure-from" label="From">
            <Input
              id="failure-from"
              type="date"
              value={from}
              onChange={(e) => onChange({ from: e.target.value || null })}
              aria-label="Filter failures from date"
            />
          </FormField>
          <FormField htmlFor="failure-to" label="To">
            <Input
              id="failure-to"
              type="date"
              value={to}
              onChange={(e) => onChange({ to: e.target.value || null })}
              aria-label="Filter failures to date"
            />
          </FormField>
        </div>
        <div className="flex gap-space-2 mt-space-2">
          <button
            type="button"
            className="text-small text-gold underline"
            onClick={() => {
              // Both ends read in the viewer's zone. `setDate` was local
              // arithmetic serialised in UTC, so the window was a day off
              // every evening and the two ends could disagree across a DST
              // change — a 30-day button spanning 31.
              onChange({
                from: isoDateInTz(new Date(Date.now() - 30 * 86_400_000), tz),
                to: isoDateInTz(new Date(), tz),
              });
            }}
          >
            Last 30 days
          </button>
          <button
            type="button"
            className="text-small text-gold underline"
            onClick={() => {
              onChange({
                from: isoDateInTz(new Date(Date.now() - 90 * 86_400_000), tz),
                to: isoDateInTz(new Date(), tz),
              });
            }}
          >
            Last 90 days
          </button>
        </div>
      </div>

    </Card>
  );
}
